import React, { useState } from 'react';
import { Calendar, MapPin, Users, Star, ExternalLink, Filter, TrendingUp } from 'lucide-react';

const PerformanceHistory = ({ bookings = [] }) => {
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock extended performance data since bookings might be limited
  const performanceHistory = [
    {
      id: 1,
      title: 'Festival d\'Été Paris',
      venue: 'Parc des Expositions',
      location: 'Paris, France',
      date: '2024-07-15',
      time: '20:00',
      attendees: 15000,
      status: 'completed',
      rating: 4.8,
      revenue: 25000,
      type: 'festival',
      description: 'Headliner du festival d\'été avec une performance de 90 minutes'
    },
    {
      id: 2,
      title: 'Soirée privée Entreprise',
      venue: 'Château de Versailles',
      location: 'Versailles, France',
      date: '2024-06-20',
      time: '19:30',
      attendees: 300,
      status: 'completed',
      rating: 5.0,
      revenue: 8000,
      type: 'private',
      description: 'Événement corporate exclusif dans un cadre prestigieux'
    },
    {
      id: 3,
      title: 'Concert Club Intime',
      venue: 'Le Bataclan',
      location: 'Paris, France',
      date: '2024-05-10',
      time: '21:00',
      attendees: 800,
      status: 'completed',
      rating: 4.6,
      revenue: 15000,
      type: 'concert',
      description: 'Performance acoustique dans un cadre intimiste'
    },
    {
      id: 4,
      title: 'Wedding Reception',
      venue: 'Domaine de la Roseraie',
      location: 'Lyon, France',
      date: '2024-09-22',
      time: '18:00',
      attendees: 150,
      status: 'confirmed',
      rating: null,
      revenue: 5000,
      type: 'wedding',
      description: 'Animation DJ pour réception de mariage'
    },
    {
      id: 5,
      title: 'Festival International',
      venue: 'Palais des Sports',
      location: 'Marseille, France',
      date: '2024-10-05',
      time: '22:00',
      attendees: 5000,
      status: 'pending',
      rating: null,
      revenue: 18000,
      type: 'festival',
      description: 'Participation au festival international de musique électronique'
    }
  ];

  const statusOptions = [
    { id: 'all', label: 'Tout', count: performanceHistory?.length },
    { id: 'completed', label: 'Terminé', count: performanceHistory?.filter(p => p?.status === 'completed')?.length },
    { id: 'confirmed', label: 'Confirmé', count: performanceHistory?.filter(p => p?.status === 'confirmed')?.length },
    { id: 'pending', label: 'En attente', count: performanceHistory?.filter(p => p?.status === 'pending')?.length }
  ];

  const filteredPerformances = filterStatus === 'all' 
    ? performanceHistory 
    : performanceHistory?.filter(p => p?.status === filterStatus);

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      confirmed: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    const labels = {
      completed: 'Terminé',
      confirmed: 'Confirmé',
      pending: 'En attente',
      cancelled: 'Annulé'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles?.[status]}`}>
        {labels?.[status]}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'festival':
        return '🎪';
      case 'concert':
        return '🎵';
      case 'private':
        return '🏢';
      case 'wedding':
        return '💒';
      default:
        return '🎤';
    }
  };

  // Calculate stats
  const completedPerformances = performanceHistory?.filter(p => p?.status === 'completed');
  const totalRevenue = completedPerformances?.reduce((sum, p) => sum + p?.revenue, 0);
  const totalAttendees = completedPerformances?.reduce((sum, p) => sum + p?.attendees, 0);
  const averageRating = completedPerformances?.length > 0 
    ? (completedPerformances?.reduce((sum, p) => sum + (p?.rating || 0), 0) / completedPerformances?.length)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Historique des performances</h2>
        
        {/* Performance Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <Calendar className="w-6 h-6 mr-3" />
              <div>
                <div className="text-2xl font-bold">{completedPerformances?.length}</div>
                <div className="text-blue-100 text-sm">Performances réalisées</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <TrendingUp className="w-6 h-6 mr-3" />
              <div>
                <div className="text-2xl font-bold">€{(totalRevenue / 1000)?.toFixed(0)}k</div>
                <div className="text-green-100 text-sm">Revenus générés</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <Users className="w-6 h-6 mr-3" />
              <div>
                <div className="text-2xl font-bold">{(totalAttendees / 1000)?.toFixed(1)}k</div>
                <div className="text-purple-100 text-sm">Spectateurs totaux</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <Star className="w-6 h-6 mr-3" />
              <div>
                <div className="text-2xl font-bold">{averageRating}</div>
                <div className="text-yellow-100 text-sm">Note moyenne</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="flex items-center space-x-4 bg-white p-4 rounded-lg shadow-sm">
        <Filter className="w-5 h-5 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">Filtrer par statut:</span>
        <div className="flex space-x-2">
          {statusOptions?.map((option) => (
            <button
              key={option?.id}
              onClick={() => setFilterStatus(option?.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filterStatus === option?.id
                  ? 'bg-blue-100 text-blue-800' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {option?.label} ({option?.count})
            </button>
          ))}
        </div>
      </div>
      {/* Performance List */}
      <div className="space-y-4">
        {filteredPerformances?.map((performance) => (
          <div
            key={performance?.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="text-2xl">{getTypeIcon(performance?.type)}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {performance?.title}
                    </h3>
                    <p className="text-gray-600">{performance?.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(performance?.status)}
                  {performance?.status === 'completed' && performance?.rating && (
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="ml-1 text-sm font-medium text-gray-900">
                        {performance?.rating}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(performance.date)?.toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} à {performance?.time}
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  {performance?.venue}, {performance?.location}
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  {performance?.attendees?.toLocaleString()} spectateurs
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  €{performance?.revenue?.toLocaleString()} revenus
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    Type: <span className="capitalize font-medium">{performance?.type}</span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Détails
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {filteredPerformances?.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune performance trouvée
          </h3>
          <p className="text-gray-500">
            Aucune performance ne correspond aux filtres sélectionnés.
          </p>
        </div>
      )}
    </div>
  );
};

export default PerformanceHistory;